import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Alert,
  InteractionManager,
  Modal,
  Pressable,
  TextInput,
  View,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import {
  checkAadhaar,
  clearRegistrationToken,
  confirmAadhaarConversion,
  getBlockedErrorMessage,
} from "@services/onboardingApi";
import { useOnboarding } from "./OnboardingContext";
import { aadhaarStyles as styles, placeholderColor } from "./styles";
import { useFlowNavigation } from "./useFlowNavigation";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { CheckAadhaarResult, OnboardingStackParamList } from "./types";

const AADHAAR_LENGTH = 12;

interface AadhaarVerificationProps {
  navigation: StackNavigationProp<
    OnboardingStackParamList,
    "AadhaarVerification"
  >;
}

// ─── Failure modal ────────────────────────────────────────────────────────────

interface FailureModalData {
  statusLabel: string;
  description: string;
  registeredPhone?: string;
  fullName?: string;
}

function AadhaarFailureModal({
  data,
  onClose,
}: {
  data: FailureModalData;
  onClose: () => void;
}) {
  return (
    <Modal
      visible
      transparent
      animationType="fade"
      statusBarTranslucent
      navigationBarTranslucent
      onRequestClose={onClose}
    >
      <View style={failureModalStyles.overlay}>
        <View style={failureModalStyles.card}>
          {/* Icon */}
          <View style={failureModalStyles.iconCircle}>
            <Text style={failureModalStyles.iconText}>!</Text>
          </View>

          {/* Header */}
          <Text style={failureModalStyles.title}>Verification Failed</Text>
          <Text style={failureModalStyles.description}>{data.description}</Text>

          {/* Info rows */}
          <View style={failureModalStyles.infoBox}>
            <InfoRow label="Status" value={data.statusLabel} />
            {!!data.registeredPhone && (
              <InfoRow label="Phone" value={data.registeredPhone} />
            )}
            {!!data.fullName && <InfoRow label="Name" value={data.fullName} />}
          </View>

          {/* Close button */}
          <Pressable
            style={failureModalStyles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <Text style={failureModalStyles.closeButtonText}>Close</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={failureModalStyles.infoRow}>
      <Text style={failureModalStyles.infoLabel}>{label}</Text>
      <Text style={failureModalStyles.infoValue}>{value}</Text>
    </View>
  );
}

const failureModalStyles = {
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center" as const,
    alignItems: "center" as const,
    paddingHorizontal: 24,
  },
  card: {
    backgroundColor: color.white,
    borderRadius: 16,
    padding: 24,
    width: "100%" as const,
    alignItems: "center" as const,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#FFE1E5",
    justifyContent: "center" as const,
    alignItems: "center" as const,
    marginBottom: 16,
  },
  iconText: {
    fontSize: 32,
    fontWeight: "700" as const,
    color: color.errorRed,
    lineHeight: 36,
  },
  title: {
    fontSize: 20,
    fontWeight: "700" as const,
    color: "#8B0000",
    marginBottom: 8,
    textAlign: "center" as const,
  },
  description: {
    fontSize: 14,
    color: "#666666",
    textAlign: "center" as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  infoBox: {
    width: "100%" as const,
    backgroundColor: "#F7F7F7",
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 20,
    gap: 8,
  },
  infoRow: {
    flexDirection: "row" as const,
    justifyContent: "space-between" as const,
  },
  infoLabel: {
    fontSize: 13,
    color: "#888888",
  },
  infoValue: {
    fontSize: 13,
    color: "#333333",
    fontWeight: "600" as const,
    flexShrink: 1,
    textAlign: "right" as const,
  },
  closeButton: {
    width: "100%" as const,
    borderWidth: 1.5,
    borderColor: color.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: "center" as const,
  },
  closeButtonText: {
    color: color.primary,
    fontSize: 16,
    fontWeight: "600" as const,
  },
};

// ─── Screen ───────────────────────────────────────────────────────────────────

const AadhaarVerification: React.FC<AadhaarVerificationProps> = ({
  navigation,
}) => {
  const { updateData } = useOnboarding();
  const { goToNextStep } = useFlowNavigation();
  const [aadhaar, setAadhaar] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState("");
  const [failureModal, setFailureModal] = useState<FailureModalData | null>(
    null,
  );
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      inputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (error && aadhaar.length < AADHAAR_LENGTH) {
      setError("");
    }
  }, [aadhaar, error]);

  const formatAadhaar = useCallback((text: string): string => {
    return text.replace(/[^\d]/g, "").slice(0, AADHAAR_LENGTH);
  }, []);

  const onChangeText = useCallback(
    (text: string) => {
      setAadhaar(formatAadhaar(text));
    },
    [formatAadhaar],
  );

  const showActiveDbModal = useCallback((result: CheckAadhaarResult) => {
    if (result.case !== "active_db") return;
    setFailureModal({
      statusLabel: "Active Delivery Boy",
      description:
        "This Aadhaar is already registered with an active delivery boy account.",
      registeredPhone: result.registeredPhone,
      fullName: result.fullName,
    });
  }, []);

  // Case 1.2 — aadhaar belongs to an APPROVED CP Leader. Confirm conversion,
  // then call /confirm-aadhaar-conversion (CP_TO_DB) which suspends the leader
  // and copies KYC onto the LEADER's registered phone. The current session
  // (a different phone) becomes obsolete — user must log in with the
  // registered number, so we clear the registration token and bounce back
  // to the phone-entry screen.
  const handleActiveCp = useCallback(
    (registeredPhone: string) => {
      Alert.alert(
        "Pehle se CityMall Partner",
        `Yeh Aadhaar pehle se ek CityMall Partner ke saath registered hai (${registeredPhone}). Kya aap CityMall Delivery Partner banna chahte hain?\n\nDhyaan dein: 'Haan' select karne par aapka current CityMall Partner account suspend ho jayega aur aapko apne registered number se login karna hoga.`,
        [
          { text: "Nahi", style: "cancel" },
          {
            text: "Haan",
            style: "destructive",
            onPress: async () => {
              setIsVerifying(true);
              try {
                const res = await confirmAadhaarConversion(aadhaar, "CP_TO_DB");
                Alert.alert(
                  "Login required",
                  `Aapka delivery partner account ban gaya hai. Please apne registered number ${res.targetPhoneMasked} se login karein.`,
                  [
                    {
                      text: "Theek Hai",
                      onPress: async () => {
                        await clearRegistrationToken();
                        navigation.reset({
                          index: 0,
                          routes: [{ name: "OnboardingPhone" }],
                        });
                      },
                    },
                  ],
                );
              } catch {
                // global interceptor toasts API errors
              } finally {
                setIsVerifying(false);
              }
            },
          },
        ],
      );
    },
    [aadhaar, navigation],
  );

  // Case 1.3 — aadhaar belongs to an inactive registration. Auto-revive on
  // the current session phone (REVIVE_INACTIVE) and skip the KYC steps,
  // jumping straight to spoke selection.
  const handleInactiveRevive = useCallback(async () => {
    try {
      await confirmAadhaarConversion(aadhaar, "REVIVE_INACTIVE");
      updateData({ aadhaarNumber: aadhaar });
      navigation.reset({ index: 0, routes: [{ name: "NearestHub" }] });
    } catch {
      // global interceptor toasts API errors
    }
  }, [aadhaar, navigation, updateData]);

  const handleCheckAadhaarResult = useCallback(
    async (result: CheckAadhaarResult) => {
      switch (result.case) {
        case "not_found":
          updateData({ aadhaarNumber: aadhaar });
          goToNextStep();
          return;
        case "active_db":
          showActiveDbModal(result);
          return;
        case "active_cp":
          handleActiveCp(result.registeredPhone);
          return;
        case "inactive":
          await handleInactiveRevive();
          return;
      }
    },
    [
      aadhaar,
      goToNextStep,
      handleActiveCp,
      handleInactiveRevive,
      showActiveDbModal,
      updateData,
    ],
  );

  const verifyAadhaar = useCallback(async () => {
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      const result = await checkAadhaar(aadhaar);
      await handleCheckAadhaarResult(result);
    } catch (err: unknown) {
      // Fraud-flagged aadhaar — terminal; other API errors are toasted by
      // the global axios interceptor.
      const blockedMessage = getBlockedErrorMessage(err);
      if (blockedMessage) {
        setError(blockedMessage);
      }
    } finally {
      setIsVerifying(false);
    }
  }, [isVerifying, aadhaar, handleCheckAadhaarResult]);

  // Display formatted with spaces: XXXX XXXX XXXX
  const displayAadhaar = aadhaar.replace(/(\d{4})(?=\d)/g, "$1 ");

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Aadhaar Verification"
        navigation={navigation}
        hideShadow
      />

      <View style={styles.contentContainer}>
        <Text style={styles.title}>Aadhaar Verification</Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Enter a valid Aadhaar Number
        </Text>

        <View
          style={[
            styles.inputContainer,
            isFocused && styles.inputContainerFocused,
            error ? { borderColor: color.errorRed } : undefined,
          ]}
        >
          <TextInput
            ref={inputRef}
            value={displayAadhaar}
            style={styles.input}
            maxLength={14} // 12 digits + 2 spaces
            keyboardType="number-pad"
            onChangeText={onChangeText}
            placeholder="Enter Your 12 Digit Aadhaar Card no"
            placeholderTextColor={placeholderColor}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            accessibilityLabel="Aadhaar number"
            accessibilityHint="Enter your 12 digit Aadhaar card number"
          />
        </View>

        {error ? (
          <Text variant="caption_2" style={styles.errorText}>
            {error}
          </Text>
        ) : null}

        <Pressable
          style={styles.verifyButton}
          disabled={isVerifying || aadhaar.length < AADHAAR_LENGTH}
          onPress={verifyAadhaar}
          accessibilityRole="button"
          accessibilityLabel="Verify Aadhaar"
        >
          <Text variant="title_16" style={styles.verifyButtonText}>
            Verify
          </Text>
        </Pressable>
      </View>

      {failureModal && (
        <AadhaarFailureModal
          data={failureModal}
          onClose={() => setFailureModal(null)}
        />
      )}
    </>
  );
};

export default AadhaarVerification;
